
import EVENTS from "@/constants/events";
import MODULE from "@/constants/modules";
import GlobalServices from "@/services/global";
import editMixin from "@/mixin/edit-mixin";
import moment from "moment";
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import SuccessScreen from "@/components/_universal/global/success-screen.vue";

const global = new GlobalServices();

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    SuccessScreen,
  },
  mixins: [editMixin],
  data() {
    return {
      isSavingSuccessful: false,
      isError: false,
      errorMessage: "Sorry, there was an error while reassigning this Purchase Order. Please try again.",
      selectedProject: null as any,
      uxProjectList: [] as any[],
      currentPOData: {} as any,
      currentStep: 1, // 1 = selection, 2 = confirmation
      confirmationText: '',
      isLoading: false,
      modalStatus: {
        modalName: "",
        modalTitle: "",
        data: null as any,
      },
    };
  },
  methods: {
    formatDate(date: any) {
      if (!date) return 'N/A';
      return moment(date).format('DD-MMM-YYYY');
    },

    onSelectProject(project: any) {
      this.selectedProject = project;
      this.isError = false; // Clear any previous errors
    },

    proceedToConfirmation() {
      if (!this.selectedProject) {
        this.isError = true;
        this.errorMessage = "Please select a work order to reassign the purchase order to.";
        return;
      }
      this.currentStep = 2;
    },

    goBackToSelection() {
      this.currentStep = 1;
      this.confirmationText = '';
    },

    async confirmReassignment() {
      if (this.confirmationText !== 'CONFIRM') {
        this.isError = true;
        this.errorMessage = "Please type 'CONFIRM' to proceed.";
        return;
      }

      await this.onSave(this.currentPOData);
    },

    async onSave(data: any) {
      if (!this.selectedProject) {
        this.isError = true;
        this.errorMessage = "Please select a work order to reassign the purchase order to.";
        return;
      }

      try {
        this.isLoading = true;
        this.isError = false;

        // Enhanced update object with detailed information
        const updateObj = {
          id: this.currentPOData.id,
          propertyName: "IntProjectID",
          propertyValue: this.selectedProject.id,
          displayName: "Work Order",
          displayValue: this.selectedProject.displayName,
          previousDisplayValue: this.currentPOData.businessName || this.currentPOData.name,
          previousValueID: this.currentPOData.intProjectID,
          referenceNumber: this.currentPOData.referenceNumber,
          description: `Purchase Order ${this.currentPOData.referenceNumber || this.currentPOData.id} reassigned from Work Order ${this.currentPOData.businessName || this.currentPOData.name} (ID: ${this.currentPOData.intProjectID}) to Work Order ${this.selectedProject.displayName} (ID: ${this.selectedProject.id}). Previous project: ${this.currentPOData.businessName || 'N/A'}, New project: ${this.selectedProject.displayName}. Action performed by user for purchase order reassignment with financial implications to external systems.`,
          actionId: MODULE.PURCHASEORDER.ActionId,
          moduleId: MODULE.PURCHASEORDER.ModuleId,
          employeeID: null, // Will be set by server
          invoiceId: null,
          projectID: this.selectedProject.id,
          intProjectID: this.selectedProject.id,
          parentId: this.currentPOData.intProjectID, // Set to previous work order ID (equivalent to parent ID)
          timezone: "America/Toronto",
          printCode: "Ignore",
          isUXProperty: true,
          tenantID: null, // Will be set by server
          correlationID: null, // Will be set by server
          source: 2,
          uid: 1,
          uniqueId: `${this.currentPOData.id}_PurchaseOrders_${this.currentPOData.id}_IntProjectID`,
          version: null,
          userDetails: null,
          parentActionID: null,
        } as any;

        // Call the update API to reassign the purchase order
        const result = await global.postData(updateObj);

        if (result.success) {
          this.isSavingSuccessful = true;
          this.isError = false;
          this.isLoading = false;

          // Reset form
          this.selectedProject = null;
          this.currentStep = 1;
          this.confirmationText = '';

          // Emit event to refresh the page/data
          setTimeout(() => {
            // Keep success screen visible until redirect
            // this.isSavingSuccessful = false; // Removed to keep success screen showing
            // Reload the current page to show updated details
            this.$root.$emit(EVENTS.PURCHASEORDER.Update);
            // Close modal and reload page
            window.location.reload();
          }, 3000); // Success screen stays visible until page reloads
        } else {
          this.isError = true;
          this.isSavingSuccessful = false;
          this.isLoading = false;
          this.errorMessage = result.message || "Failed to reassign purchase order. Please try again.";
        }
      } catch (error) {
        this.isError = true;
        this.isSavingSuccessful = false;
        this.isLoading = false;
        this.errorMessage = "An unexpected error occurred while reassigning the purchase order.";
      }
    },

    async initializeData() {
      // Get Data From Modal
      this.modalStatus = this.$store.getters.getModalStatus;
      this.currentPOData = this.modalStatus.data;

      // Get Projects/Work Orders List for selection
      this.uxProjectList = await global.getUXDropDown(MODULE.PROJECTS.ActionId);
      
      // Filter out the current project from the list
      if (this.currentPOData.intProjectID) {
        this.uxProjectList = this.uxProjectList.filter((project: any) => 
          project.id !== this.currentPOData.intProjectID
        );
      }
    },
  },

  async mounted() {
    // Initialize data when component is mounted
    await this.initializeData();
  },

  async created() {
    // Subscribe to save button click event on modal box
    this.$root.$on("Save" + EVENTS.PURCHASEORDER.Reassign, this.onSave);
  },

  beforeDestroy() {
    // Unsubscribe from save button click event on modal box
    this.$root.$off("Save" + EVENTS.PURCHASEORDER.Reassign, this.onSave);
  },
};
